/**
 * Deeplink Switching summary page.
 *
 * Mounted at `/deeplink/switching/:code/summary/SWITCHING` (delegated from
 * `DeeplinkSummary/index.js`).
 *
 * Reads the switch payload (`from`, `to`, `amount`, `cifid`, ...) from the
 * `switchingReducer.resource` slice (populated by `DeepLink/switching.js` via
 * `setResource` before navigation). Reuses the same `setTransfer` saga used by
 * `/switching/summary`, so error handling (FNZ timeout, duplicate transaction)
 * is identical.
 */

import { Checkbox } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "underscore";
import {
  AlertInfo,
  BackButton,
  CustomButton,
  DefaultLoader,
} from "../../components";
import { dataLayerGTM } from "../../utils/functions/dataLayerGTM";
import {
  resetTransferError,
  setTransfer,
} from "../switching/redux/action";
import { FNZ_TIMEOUT, TRANSACTION_TIMEOUT } from "../switching/redux/constant";
import "../switching/style.summary.scss";

const principalTnCPDF =
  "https://www.principal.com.my/sites/default/files/pdf-uploads/Principal%20Mini%20Program%20%28IMMF%20Class%20D%29%20Terms%20and%20Conditions.pdf";
const riskDisclosureUrl =
  "https://www.principal.com.my/en/ut-loan-financing-risk-my/mv";

const isObjectNotEmpty = obj => {
  return obj && typeof obj === "object" && Object.keys(obj).length > 0;
};

const useModal = () => {
  const [open, setOpen] = useState(false);
  const modalOpen = () => setOpen(true);
  const modalClose = () => setOpen(false);

  return { open, modalOpen, modalClose };
};

function isResourceValid(resource) {
  if (!resource || !isObjectNotEmpty(resource)) return false;
  if (!resource.cifid) return false;

  const fromOk =
    resource.from &&
    resource.from.cifPlanId &&
    resource.from.fundCd &&
    resource.from.name;
  const toOk =
    resource.to &&
    resource.to.cifPlanId &&
    resource.to.fundCd &&
    resource.to.name;

  return Boolean(fromOk && toOk && resource.amount);
}

function DeepLinkSwitchingSummary(props) {
  const {
    history,
    resource,
    transferError,
    transferLoading,
    setTransfer,
    resetTransferError,
  } = props;

  const [isCheck, setCheck] = useState(false);
  // Once the user submits the transfer, ignore the resource-validity guard
  // below so that a subsequent redux clear (or navigation) cannot kick us
  // back to "/" before the /switching/success push takes effect.
  const hasSubmitted = useRef(false);

  const {
    open: isSalesChargeDisclaimerOpen,
    modalOpen: openSalesChargeDisclaimerModal,
    modalClose: closeSalesChargeDisclaimerModal,
  } = useModal();

  // If the redux resource is missing/invalid (eg. user opened this URL directly,
  // or the state was cleared), bail back to the deeplink entry.
  // Skipped once the user has submitted so the post-transfer navigation to
  // /switching/success is not interrupted.
  useEffect(() => {
    if (hasSubmitted.current) return;
    if (!isResourceValid(resource)) {
      history.replace("/");
    }
  }, [resource]);

  if (!hasSubmitted.current && !isResourceValid(resource)) return null;
  if (transferLoading) return <DefaultLoader />;

  const principalTnC = () => {
    window.my.showLoading();
    window.my.postMessage({
      api: "downloadFile",
      pdf: principalTnCPDF,
      event: "openPdf",
    });
  };

  const generateTransferPayload = () => {
    const payload = {
      cifId: resource.cifid,
      from: {
        cifPlanId: resource.from.cifPlanId,
        amount: +resource.amount,
        amountCcy: "MYR",
        fundCd: resource.from.fundCd,
      },
      to: {
        cifPlanId: resource.to.cifPlanId,
        amount: +resource.amount,
        amountCcy: "MYR",
        fundCd: resource.to.fundCd,
      },
    };

    if (resource.maxAmount && +resource.amount >= +resource.maxAmount) {
      payload.from.percentage = 100;
      payload.to.percentage = 100;
    }

    return payload;
  };

  const handleNext = () => {
    dataLayerGTM(
      "click_tag",
      "Click",
      "Button",
      "Deeplink Switching Summary - Confirm"
    );

    const payload = generateTransferPayload();

    hasSubmitted.current = true;
    setTransfer(payload, () => {
      history.push("/switching/success");
    });
  };

  return (
    <>
      <AlertInfo
        description="We are facing some technical issues, and we are working very hard to resolve the issue."
        isOpen={
          Boolean(transferError)
        }
        isTimeout
        handleAction={() => {
          resetTransferError();
        }}
      />
      <AlertInfo
        title="Switching Fee"
        isOpen={isSalesChargeDisclaimerOpen}
        handleAction={closeSalesChargeDisclaimerModal}
      >
        <ul style={{ marginLeft: "15px", paddingLeft: "15px" }}>
          <li>
            When you switch between classes or into another Principal Malaysia
            fund (or its classes), it's treated as a withdrawal from one and an
            investment in another.
          </li>
          <li>
            You might incur a Switching Fee, which is the difference between the
            Application Fee of the current class and the one you're switching
            into.
          </li>
          <li>
            No Switching Fee will be charged if the new class or Principal
            Malaysia fund (or its classes) has a lower Application Fee.
          </li>
          <li>There may be an administrative fee for each switch.</li>
        </ul>
        <span style={{ display: "inline-block", marginTop: "18px" }}>
          For more details, please refer to the Annexure of the respective
          class.
        </span>
      </AlertInfo>

      <div className="l-summary">
        <BackButton
          handleBack={() => history.back()}
          title="Your Switch Summary"
          className="c-back-btn--space-left"
        />
        <div className="l-summary__content">
          <h5>Switch From:</h5>
          <h5>{resource.from.name}</h5>
          <div className="l-summary__separator" />
          <h5>Switch To:</h5>
          <h5>{resource.to.name}</h5>
          <p>
            A switching fee may apply. Final charges will be confirmed after the
            switch.{" "}
            <span onClick={openSalesChargeDisclaimerModal}>More info</span>
          </p>
        </div>

        <div className="l-summary__amount">
          <h4>Total Switch Amount</h4>
          <h1>
            RM{" "}
            <NumberFormat
              value={resource.amount}
              thousandSeparator
              displayType="text"
              decimalScale={2}
              fixedDecimalScale={true}
            />
          </h1>
        </div>

        <div className="l-summary__footer">
          <div className="l-summary__terms">
            <Checkbox
              checked={isCheck}
              onChange={() => setCheck(!isCheck)}
              color="primary"
              sx={{
                margin: 0,
                padding: "0px !important",
                paddingTop: 0.5,
                "& .MuiSvgIcon-root": { fontSize: 24 },
              }}
            />
            <div>
              <p style={{ marginBottom: "8px" }}>
                I have read and agreed to{" "}
                <span onClick={principalTnC}>
                  Principal’s Terms & Conditions
                </span>
                , the Unit Trust Loan Financing{" "}
                <span
                  onClick={() =>
                    window.my.navigateTo({
                      url:
                        "/pages/link/link?url=" +
                        encodeURIComponent(riskDisclosureUrl),
                    })
                  }
                >
                  Risk Disclosure Statement
                </span>
                , and have read and understood the relevant Prospectus,
                Information Memorandum and/or Disclosure Document including any
                supplemental thereof and the Product Highlight Sheet (if any)
                before Investing.
              </p>
              <p>
                I acknowledge the investment risk, and aware that the fund is
                not E-money, deposit, or capital guaranteed/protected by PIDM.
                Losses are not covered by the Capital Market Compensation Fund.
              </p>
            </div>
          </div>

          <CustomButton
            text="Next"
            variant="contained"
            color="#fff"
            backgroundColor="#1278CC"
            disabled={!isCheck || transferLoading}
            onClick={handleNext}
          />
        </div>
      </div>
    </>
  );
}

const withConnect = connect(
  state => ({
    resource: state.switchingReducer.resource,
    transferError: state.switchingReducer.transferError,
    transferLoading: state.switchingReducer.transferLoading,
  }),
  {
    setTransfer,
    resetTransferError,
  }
);

export default compose(
  withRouter,
  withConnect
)(DeepLinkSwitchingSummary);
